import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { UserAuthProvider } from "@/contexts/UserAuthContext";
import Layout from "@/components/Layout";
import ScrollToTop from "@/components/ScrollToTop";
import Home from "@/pages/Home";
import Events from "@/pages/Events";
import EventBooking from "@/pages/EventBooking";
import BookingSuccess from "@/pages/BookingSuccess";
import Shop from "@/pages/Shop";
import HowItsMade from "@/pages/HowItsMade";
import Community from "@/pages/Community";
import RefundPolicy from "@/pages/RefundPolicy";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import PetRecord from "@/pages/PetRecord";
import PublicPetRecord from "@/pages/PublicPetRecord";
import AuthCallback from "@/pages/AuthCallback";

const RequireAdmin = ({ children }) => {
  const { token, loading } = useAuth();
  if (loading) return null;
  if (!token) return <Navigate to="/admin/login" replace />;
  return children;
};

function AppRoutes() {
  const location = useLocation();
  // CRITICAL: If returning from Google OAuth, session_id is in URL fragment.
  // Handle it synchronously here BEFORE the normal auth check runs.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<Home />} />
        <Route path="/events" element={<Events />} />
        <Route path="/events/:id" element={<EventBooking />} />
        <Route path="/booking/:id/success" element={<BookingSuccess />} />
        <Route path="/shop" element={<Shop />} />
        <Route path="/how-its-made" element={<HowItsMade />} />
        <Route path="/community" element={<Community />} />
        <Route path="/pet-record" element={<PetRecord />} />
        <Route path="/refund-policy" element={<RefundPolicy />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      </Route>
      <Route path="/p/:shareId" element={<PublicPetRecord />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route
        path="/admin/*"
        element={
          <RequireAdmin>
            <AdminDashboard />
          </RequireAdmin>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <div className="App" data-testid="app-root">
      <AuthProvider>
        <UserAuthProvider>
          <BrowserRouter>
            <ScrollToTop />
            <Toaster position="top-right" richColors />
            <AppRoutes />
          </BrowserRouter>
        </UserAuthProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
